import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Gauge, Database, Sparkle, CheckCircle, Warning, ArrowsClockwise, ClockClockwise } from '@phosphor-icons/react';

/**
 * Admin-only system audit page. Runs the data-quality and performance-audit
 * backend endpoints with a single click and renders the results readably.
 * Wired into the sidebar under /admin/audit.
 */
export default function AuditPage() {
  const [dq, setDq] = useState(null);
  const [perf, setPerf] = useState(null);
  const [dqLoading, setDqLoading] = useState(false);
  const [perfLoading, setPerfLoading] = useState(false);
  const [dqError, setDqError] = useState(null);
  const [perfError, setPerfError] = useState(null);
  // Backfill job state
  const [backfillJob, setBackfillJob] = useState(null);
  const [backfillStarting, setBackfillStarting] = useState(false);

  const runBackfill = async () => {
    if (!window.confirm(
      "Backfill the 'last call / email / meeting' fields on every contact?\n\n" +
      "This is a one-time job. It walks every activity in the database and " +
      "stamps the corresponding date on each contact so Pulse-history lookups " +
      "(\"haven't been called in 90 days\") can run instantly on existing data.\n\n" +
      "Safe to run any time. Takes 1-3 minutes on 250K contacts."
    )) return;
    setBackfillStarting(true);
    try {
      const { data: job } = await api.post('/admin/backfill-last-activity');
      setBackfillJob(job);
      pollBackfill(job.job_id);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBackfillStarting(false);
    }
  };

  const pollBackfill = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const { data: job } = await api.get(`/admin/backfill-last-activity-status/${jobId}`);
        setBackfillJob(job);
        if (job.status === 'complete' || job.status === 'error') {
          clearInterval(interval);
        }
      } catch (err) {
        console.warn('Backfill poll error', err);
      }
    }, 2000);
  };

  const runDataQuality = async () => {
    setDqLoading(true);
    setDqError(null);
    try {
      const { data } = await api.get('/admin/data-quality-audit');
      setDq(data);
    } catch (err) {
      setDqError(err.response?.data?.detail || err.message);
    } finally {
      setDqLoading(false);
    }
  };

  const runPerformance = async () => {
    setPerfLoading(true);
    setPerfError(null);
    try {
      const { data } = await api.get('/admin/performance-audit');
      setPerf(data);
    } catch (err) {
      setPerfError(err.response?.data?.detail || err.message);
    } finally {
      setPerfLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="audit-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Sparkle size={24} weight="fill" className="text-[#F39C12]" />
          System Audit
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          One-click audits of data quality and database performance. Run on production for real numbers.
        </p>
      </div>

      {/* One-shot Backfill */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClockClockwise size={20} weight="bold" className="text-amber-700" />
            <h2 className="font-bold text-amber-900">Backfill Pulse-History Fields</h2>
          </div>
          {!backfillJob && (
            <button
              onClick={runBackfill}
              disabled={backfillStarting}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold rounded flex items-center gap-1.5"
              data-testid="run-backfill-btn"
            >
              {backfillStarting ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
              {backfillStarting ? 'Starting…' : 'Run one-time backfill'}
            </button>
          )}
        </div>
        <div className="p-5 text-sm">
          <p className="text-amber-900 mb-2">
            Run this <strong>once</strong> after deploying v2.28+. It populates the new <code className="bg-amber-100 px-1 rounded text-[11px]">last_call_at</code>,
            <code className="bg-amber-100 px-1 rounded text-[11px] ml-1">last_email_at</code>, <code className="bg-amber-100 px-1 rounded text-[11px]">last_meeting_at</code>,
            and <code className="bg-amber-100 px-1 rounded text-[11px]">last_activity_at</code> fields on every contact based on existing activities.
          </p>
          <p className="text-amber-800 text-xs">
            Without this, the new "hasn't been called in N days" lookups will work for activities logged from now on, but won't see your historical activity data.
          </p>
          {backfillJob && (
            <div className="mt-3 bg-white border border-amber-300 rounded p-3" data-testid="backfill-progress">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-slate-800">
                  {backfillJob.status === 'running' && '⏳ Backfilling…'}
                  {backfillJob.status === 'complete' && '✅ Backfill complete'}
                  {backfillJob.status === 'error' && '❌ Backfill failed'}
                </div>
                <div className="text-[11px] text-slate-600 font-mono">{backfillJob.progress_pct?.toFixed(1) || 0}%</div>
              </div>
              <div className="h-2 bg-slate-200 rounded overflow-hidden mb-2">
                <div
                  className={`h-full transition-all ${backfillJob.status === 'error' ? 'bg-red-500' : 'bg-amber-500'}`}
                  style={{ width: `${backfillJob.progress_pct || 0}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-600 grid grid-cols-2 gap-x-3">
                <span>Processed: <strong>{backfillJob.contacts_processed?.toLocaleString()}</strong> / {backfillJob.total_contacts?.toLocaleString()}</span>
                <span>Updated: <strong>{backfillJob.contacts_updated?.toLocaleString()}</strong></span>
              </div>
              {backfillJob.error_message && (
                <div className="mt-2 text-[11px] text-red-600 break-all">Error: {backfillJob.error_message}</div>
              )}
              {(backfillJob.status === 'complete' || backfillJob.status === 'error') && (
                <button
                  onClick={() => setBackfillJob(null)}
                  className="mt-2 px-2 py-1 text-[10px] bg-slate-100 border border-slate-300 rounded hover:bg-slate-200"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Quality */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database size={20} weight="bold" className="text-[#0B5394]" />
              <h2 className="font-bold text-slate-800">Data Quality Audit</h2>
            </div>
            <button
              onClick={runDataQuality}
              disabled={dqLoading}
              className="px-3 py-1.5 bg-[#0B5394] hover:bg-[#094180] disabled:opacity-50 text-white text-xs font-semibold rounded flex items-center gap-1.5"
              data-testid="run-data-quality-btn"
            >
              {dqLoading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
              {dqLoading ? 'Auditing…' : (dq ? 'Re-run' : 'Run Audit')}
            </button>
          </div>
          <div className="p-5 text-sm">
            {!dq && !dqLoading && !dqError && (
              <p className="text-slate-500 italic">Click <strong>Run Audit</strong> to scan your contacts collection for duplicates, malformed emails, and missing fields.</p>
            )}
            {dqError && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-xs">
                <strong>Error:</strong> {dqError}
              </div>
            )}
            {dq && <DataQualityResults dq={dq} />}
          </div>
        </div>

        {/* Performance */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gauge size={20} weight="bold" className="text-[#0B5394]" />
              <h2 className="font-bold text-slate-800">Performance Audit</h2>
            </div>
            <button
              onClick={runPerformance}
              disabled={perfLoading}
              className="px-3 py-1.5 bg-[#0B5394] hover:bg-[#094180] disabled:opacity-50 text-white text-xs font-semibold rounded flex items-center gap-1.5"
              data-testid="run-performance-btn"
            >
              {perfLoading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
              {perfLoading ? 'Timing…' : (perf ? 'Re-run' : 'Run Audit')}
            </button>
          </div>
          <div className="p-5 text-sm">
            {!perf && !perfLoading && !perfError && (
              <p className="text-slate-500 italic">Click <strong>Run Audit</strong> to time canonical queries and verify indexes are working.</p>
            )}
            {perfError && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-xs">
                <strong>Error:</strong> {perfError}
              </div>
            )}
            {perf && <PerformanceResults perf={perf} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, hint, warn }) {
  return (
    <div className="flex justify-between items-baseline py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 text-xs">{label}</span>
      <span className={`font-bold text-sm ${warn ? 'text-red-600' : 'text-slate-800'}`}>
        {value}
        {hint && <span className="text-[10px] text-slate-400 ml-1">{hint}</span>}
      </span>
    </div>
  );
}

function DataQualityResults({ dq }) {
  const total = dq.total_contacts || 1;
  const dupExtra = dq.duplicate_emails?.extra_rows_collapsable || 0;
  const malformed = dq.email?.malformed_in_50k_sample || 0;
  const noContact = dq.missing_critical_fields?.no_phone_and_no_email || 0;
  const nameDupExtra = dq.likely_duplicates_by_name_company?.extra_rows_collapsable || 0;

  return (
    <div className="space-y-4" data-testid="data-quality-results">
      <div className="bg-blue-50 border border-blue-200 rounded p-3">
        <div className="text-blue-900 font-bold text-base">{dq.total_contacts.toLocaleString()} contacts</div>
        <div className="text-blue-700 text-[11px]">Audit completed in {dq.elapsed_seconds}s</div>
      </div>

      <Section title="Email Quality">
        <StatRow label="With email" value={dq.email.with_email.toLocaleString()} hint={`(${(dq.email.with_email / total * 100).toFixed(1)}%)`} />
        <StatRow label="Missing email" value={dq.email.missing_email.toLocaleString()} warn={dq.email.missing_email > total * 0.2} />
        <StatRow label="Malformed (in 50K sample)" value={malformed.toLocaleString()} warn={malformed > 0} />
        {dq.email.malformed_samples?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Examples:</div>
            {dq.email.malformed_samples.slice(0, 5).map((s, i) => (
              <div key={i} className="font-mono text-[11px] text-red-600">{s.email}</div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Duplicate Emails (full DB scan)">
        <StatRow label="Unique emails appearing >1 time" value={dq.duplicate_emails.unique_emails_with_duplicates.toLocaleString()} warn={dq.duplicate_emails.unique_emails_with_duplicates > 0} />
        <StatRow label="Extra duplicate rows (collapsable)" value={dupExtra.toLocaleString()} warn={dupExtra > 0} />
        {dq.duplicate_emails.top_offenders?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Top offenders:</div>
            {dq.duplicate_emails.top_offenders.slice(0, 5).map((d, i) => (
              <div key={i} className="flex justify-between text-[11px] py-0.5">
                <span className="font-mono text-slate-700 truncate mr-2">{d.email}</span>
                <span className="text-red-600 font-bold shrink-0">×{d.count}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Missing Critical Fields">
        <StatRow label="No first AND no last name" value={dq.missing_critical_fields.no_first_or_last_name.toLocaleString()} warn={dq.missing_critical_fields.no_first_or_last_name > 0} />
        <StatRow label="Unreachable (no phone, no email)" value={noContact.toLocaleString()} warn={noContact > total * 0.05} />
        <StatRow label="Missing company" value={dq.missing_critical_fields.no_company.toLocaleString()} hint={`(${(dq.missing_critical_fields.no_company / total * 100).toFixed(1)}%)`} />
      </Section>

      <Section title="Likely-Duplicate Contacts (name + company)">
        <StatRow label="Unique combos w/ duplicates" value={dq.likely_duplicates_by_name_company.unique_combos_with_duplicates.toLocaleString()} warn={dq.likely_duplicates_by_name_company.unique_combos_with_duplicates > 0} />
        <StatRow label="Extra rows (collapsable)" value={nameDupExtra.toLocaleString()} warn={nameDupExtra > 0} />
        {dq.likely_duplicates_by_name_company.top_offenders?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Top offenders:</div>
            {dq.likely_duplicates_by_name_company.top_offenders.slice(0, 5).map((d, i) => (
              <div key={i} className="flex justify-between text-[11px] py-0.5">
                <span className="text-slate-700 truncate mr-2">
                  {d.first_name} {d.last_name}{d.company ? ` @ ${d.company}` : ''}
                </span>
                <span className="text-red-600 font-bold shrink-0">×{d.count}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Status Distribution">
        {dq.status_distribution.map((s, i) => (
          <StatRow key={i} label={s.status} value={s.count.toLocaleString()} />
        ))}
      </Section>
    </div>
  );
}

function PerformanceResults({ perf }) {
  const slow = perf.summary.slow_queries_over_1s;
  const failed = perf.summary.failed_queries;

  return (
    <div className="space-y-4" data-testid="performance-results">
      <div className={`border rounded p-3 ${slow > 0 || failed > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
        <div className="flex items-center gap-2 font-bold text-base">
          {slow === 0 && failed === 0 ? (
            <><CheckCircle size={18} weight="fill" className="text-green-600" /><span className="text-green-900">All queries healthy</span></>
          ) : (
            <><Warning size={18} weight="fill" className="text-yellow-600" /><span className="text-yellow-900">{slow} slow, {failed} failed</span></>
          )}
        </div>
        <div className="text-[11px] mt-1">
          {perf.total_contacts.toLocaleString()} contacts · slowest: <strong>{perf.summary.slowest_query_name}</strong> ({perf.summary.slowest_query_ms.toFixed(0)}ms)
        </div>
      </div>

      <Section title="Query Timings">
        {perf.queries.map((q, i) => (
          <div key={i} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-slate-700 text-xs flex-1 mr-2 truncate">{q.name}</span>
            <span className={`font-bold text-xs shrink-0 ${
              !q.ok ? 'text-red-600'
              : q.ms > 1000 ? 'text-red-600'
              : q.ms > 300 ? 'text-yellow-600'
              : 'text-green-700'
            }`}>
              {q.ok ? `${q.ms.toFixed(0)}ms` : `❌ ${q.error?.slice(0, 30)}`}
            </span>
          </div>
        ))}
      </Section>

      <Section title="Indexes on Contacts">
        <div className="text-[11px] space-y-1">
          {perf.indexes_on_contacts.map((ix, i) => (
            <div key={i} className="font-mono text-slate-600">
              {ix.name}: <span className="text-slate-500">{Object.keys(ix.key).join(', ')}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{title}</div>
      <div className="bg-slate-50 rounded p-2.5">{children}</div>
    </div>
  );
}
